import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BadgeCheck,
  Ban,
  CheckCircle2,
  Copy,
  IdCard,
  Info,
  MailPlus,
  Monitor,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Smartphone,
  UserCheck,
  UserMinus,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import DepartmentSelector from '@/components/DepartmentSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { CommanServices } from '@/services/commanServices';
import { SaaSServices } from '@/services/saasServices';
import {
  apiErrorMessage,
  canManageInstitutionUsers,
  readStoredUser,
  unwrapApiData,
} from '@/auth/saasSession';
import { INSTITUTION_ROLE_OPTIONS } from '@/auth/saasCatalog';
import {
  activationCandidateIds,
  filterUsersBySmartSearch,
  mergeUsersWithMemberships,
  normalizeUsersAccessPayload,
  postCloseAccessForInstitutionRole,
  postCloseAccessLabel,
} from './usersAccessModel';

const ROLE_HELP = {
  institution_admin: 'Manages this institution’s members, configuration and local policies within the SaaS boundary.',
  security_admin_delegate: 'Manages delegated security, access reviews, audit records and approved support access.',
  executive_oversight: 'Receives organization-wide dashboards, status and reports; read-only by default.',
  compliance_auditor: 'Reviews audit records and retained information according to the institution retention policy.',
};

const EMPTY_FORM = {
  user_account: '',
  full_name: '',
  email: '',
  phone_number: '',
  employee_id: '',
  password: '',
  departments_units: [],
  enable_nearby_awareness: false,
  is_active: false,
  institution_roles: [],
  web_enabled: false,
  mobile_enabled: true,
};

const EMPTY_INVITE = {
  email: '',
  institution_roles: [],
  departments_units: [],
  web_enabled: false,
  mobile_enabled: true,
  expires_in_hours: 72,
};

const userLabel = (user) => user?.full_name?.trim()
  || user?.email
  || user?.user_account
  || 'Unnamed user';

const roleLabel = (value) => INSTITUTION_ROLE_OPTIONS.find((role) => role.value === value)?.label
  || String(value || '').replace(/_/g, ' ');

const formForUser = (user) => ({
  ...EMPTY_FORM,
  user_account: user.user_account || user.email || '',
  full_name: user.full_name || '',
  email: user.email || user.user_account || '',
  phone_number: user.phone_number || '',
  employee_id: user.employee_id || '',
  departments_units: Array.isArray(user.departments_units) ? user.departments_units : [],
  enable_nearby_awareness: user.enable_nearby_awareness === true,
  is_active: user.account_status === 'active',
  institution_roles: Array.isArray(user.institution_roles) ? user.institution_roles : [],
  web_enabled: user.web_enabled === true,
  mobile_enabled: user.mobile_enabled === true,
});

const statusStyle = (status) => {
  if (status === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (status === 'inactive') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (status === 'suspended' || status === 'blocked') return 'border-red-200 bg-red-50 text-red-700';
  return 'border-slate-200 bg-slate-100 text-slate-600';
};

const RoleSelector = ({ selected, onChange, onRoleInfo, disabled = false }) => (
  <div className="grid gap-3">
    {INSTITUTION_ROLE_OPTIONS.map((role) => {
      const checked = selected.includes(role.value);
      return (
        <div key={role.value} className={`rounded-xl border p-3 transition ${checked ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-start gap-3">
            <label className={`flex min-w-0 flex-1 items-start gap-3 ${disabled ? 'cursor-default' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                aria-label={role.label}
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(checked
                  ? selected.filter((value) => value !== role.value)
                  : [...selected, role.value])}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-900">{role.label}</span>
              <span className="mt-1 block text-xs leading-relaxed text-slate-500">{ROLE_HELP[role.value]}</span>
              </span>
            </label>
            <button
              type="button"
              aria-label={`Information about ${role.label}`}
              title={`Information about ${role.label}`}
              onClick={() => onRoleInfo?.(role)}
              className="rounded-full p-1 text-slate-400 transition hover:bg-white hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    })}
  </div>
);

const previewValue = (preview, keys, fallback = 0) => {
  for (const key of keys) {
    if (preview?.[key] !== undefined && preview?.[key] !== null) return Number(preview[key]) || 0;
  }
  return fallback;
};

export default function UsersAccess() {
  const currentUser = readStoredUser() || {};
  const canMutateUsers = canManageInstitutionUsers(currentUser);
  const [rawUsers, setRawUsers] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [retentionDays, setRetentionDays] = useState(null);
  const [seatSummary, setSeatSummary] = useState({ used: 0, limit: null, available: null, webEnabled: 0, mobileEnabled: 0, pendingInvitations: 0 });
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState(EMPTY_INVITE);
  const [issuedInvitation, setIssuedInvitation] = useState(null);
  const [activation, setActivation] = useState({ open: false, scope: 'all', ids: undefined, preview: null, loading: false });
  const [removeTarget, setRemoveTarget] = useState(null);
  const [roleInfo, setRoleInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [accessRes, membershipRes, invitationRes, usersRes, retentionRes] = await Promise.all([
      SaaSServices.getUsersAccess(),
      SaaSServices.listMemberships(),
      SaaSServices.listInvitations(),
      CommanServices.getuserconfigs(),
      SaaSServices.getRetentionPolicy(),
    ]);
    const fallbackMemberships = unwrapApiData(membershipRes) || [];
    const fallbackInvitations = unwrapApiData(invitationRes) || [];
    const normalized = normalizeUsersAccessPayload(
      unwrapApiData(accessRes) || {},
      fallbackMemberships,
      fallbackInvitations,
    );
    setMemberships(normalized.memberships);
    setInvitations(normalized.invitations);
    setSeatSummary(normalized.seatSummary);
    setRawUsers(unwrapApiData(usersRes) || []);
    setRetentionDays(unwrapApiData(retentionRes)?.retention_days || {});
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const allUsers = useMemo(
    () => mergeUsersWithMemberships(rawUsers, memberships)
      .filter((user) => user.account_status !== 'removed'),
    [rawUsers, memberships],
  );
  const filteredUsers = useMemo(
    () => filterUsersBySmartSearch(allUsers, search),
    [allUsers, search],
  );
  const selectedUser = useMemo(
    () => allUsers.find((user) => user.id === selectedUserId) || null,
    [allUsers, selectedUserId],
  );
  const pendingInvitations = useMemo(
    () => invitations.filter((row) => row.status === 'pending'),
    [invitations],
  );

  useEffect(() => {
    if (!creating && selectedUser) setForm(formForUser(selectedUser));
  }, [creating, selectedUser]);

  const beginAdd = () => {
    setSelectedUserId(null);
    setCreating(true);
    setForm(EMPTY_FORM);
  };

  const beginEdit = (user) => {
    setCreating(false);
    setSelectedUserId(user.id);
    setForm(formForUser(user));
  };

  const updateForm = (key, value) => setForm((old) => ({ ...old, [key]: value }));

  const saveUser = async () => {
    const email = String(form.email || form.user_account || '').trim().toLowerCase();
    if (!email || !email.includes('@')) return toast.error('Enter a valid email address.');
    if (creating && String(form.password || '').length < 3) return toast.error('Set a temporary password of at least three characters.');
    setSaving(true);
    const profilePayload = {
      user_account: email,
      full_name: String(form.full_name || '').trim(),
      phone_number: String(form.phone_number || '').trim(),
      email,
      employee_id: String(form.employee_id || '').trim(),
      access_level: 'Reporter',
      departments_units: form.departments_units || [],
      enable_nearby_awareness: form.enable_nearby_awareness === true,
      ...(String(form.password || '').trim() ? { password: form.password.trim() } : {}),
      ...(creating ? {
        is_active: false,
        role: form.institution_roles.includes('institution_admin') ? 'admin' : 'user',
      } : {}),
    };

    const profileRes = creating
      ? await CommanServices.setuserconfigs(profilePayload)
      : await CommanServices.updateuserconfigs(selectedUser.id, profilePayload);
    const savedUser = unwrapApiData(profileRes);
    if (!savedUser) {
      setSaving(false);
      return toast.error(apiErrorMessage(profileRes, 'The user could not be saved.'));
    }

    const membershipId = selectedUser?.membership_id
      || savedUser?.membership?.id
      || savedUser?.membership_id;
    if (membershipId) {
      const nextMembershipStatus = form.is_active
        ? 'active'
        : creating
          ? 'inactive'
          : ['inactive', 'suspended', 'blocked'].includes(selectedUser?.account_status)
            ? selectedUser.account_status
            : 'suspended';
      const membershipRes = await SaaSServices.updateMembership(membershipId, {
        status: nextMembershipStatus,
        institution_roles: form.institution_roles,
        web_enabled: form.web_enabled,
        mobile_enabled: form.mobile_enabled,
        departments_units: form.departments_units || [],
        enable_nearby_awareness: form.enable_nearby_awareness === true,
      });
      if (!unwrapApiData(membershipRes)) {
        setSaving(false);
        await load();
        return toast.error(apiErrorMessage(membershipRes, 'The profile was saved, but institution access could not be updated.'));
      }
    }
    const savedId = selectedUser?.id || savedUser?.id;
    await load();
    setCreating(false);
    setSelectedUserId(savedId || null);
    setSaving(false);
    toast.success(creating ? 'User added to this institution' : 'User and access settings updated');
  };

  const updateStatus = async (user, active) => {
    if (!user?.membership_id) return;
    setSaving(true);
    const response = await SaaSServices.updateMembership(user.membership_id, {
      status: active ? 'active' : 'suspended',
    });
    setSaving(false);
    if (!unwrapApiData(response)) return toast.error(apiErrorMessage(response));
    await load();
    toast.success(active ? 'User activated' : 'Institution access suspended');
  };

  const removeMembership = async () => {
    if (!removeTarget?.membership_id) return;
    setSaving(true);
    const response = await SaaSServices.revokeMembership(removeTarget.membership_id);
    setSaving(false);
    if (!unwrapApiData(response)) return toast.error(apiErrorMessage(response));
    if (selectedUserId === removeTarget.id) {
      setSelectedUserId(null);
      setCreating(false);
      setForm(EMPTY_FORM);
    }
    setRemoveTarget(null);
    await load();
    toast.success('User removed from this institution. Their platform identity was preserved.');
  };

  const createInvitation = async (event) => {
    event.preventDefault();
    if (!Array.isArray(invite.departments_units) || invite.departments_units.length === 0) {
      return toast.error('Select at least one department or unit for this invitation.');
    }
    setSaving(true);
    const response = await SaaSServices.createInvitation(invite);
    const created = unwrapApiData(response);
    setSaving(false);
    if (!created) return toast.error(apiErrorMessage(response));
    setInvite(EMPTY_INVITE);
    setInviteOpen(false);
    await load();
    if (created.invitation_token) {
      const url = `${window.location.origin}/accept-invitation?token=${encodeURIComponent(created.invitation_token)}`;
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch (_) { /* manual copy remains available */ }
      setIssuedInvitation({ ...created, acceptance_url: url, copied });
      toast.success(copied
        ? 'Invitation link copied. Send it to the user.'
        : 'Invitation created. Copy the link and send it to the user.');
    } else {
      toast.success('Invitation created');
    }
  };

  const revokeInvitation = async (invitationId) => {
    const response = await SaaSServices.revokeInvitation(invitationId);
    if (!unwrapApiData(response)) return toast.error(apiErrorMessage(response));
    await load();
    toast.success('Pending invitation revoked');
  };

  const copyInvitation = async (row) => {
    if (!row.invitation_token) return toast.error('The one-time token is no longer available.');
    const url = `${window.location.origin}/accept-invitation?token=${encodeURIComponent(row.invitation_token)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Acceptance link copied');
    } catch (_) {
      toast.error('Could not copy the link.');
    }
  };

  const openActivationPreview = async (scope, user = null) => {
    const ids = scope === 'all'
      ? undefined
      : scope === 'filtered'
        ? activationCandidateIds(filteredUsers)
        : [user?.membership_id].filter(Boolean);
    if (Array.isArray(ids) && !ids.length) return toast.info('No eligible inactive users are in this selection.');
    setActivation({ open: true, scope, ids, preview: null, loading: true });
    const response = await SaaSServices.previewMembershipActivation(ids);
    const preview = unwrapApiData(response);
    if (!preview) {
      setActivation({ open: false, scope, ids, preview: null, loading: false });
      return toast.error(apiErrorMessage(response, 'Activation could not be previewed.'));
    }
    setActivation({ open: true, scope, ids, preview, loading: false });
  };

  const confirmActivation = async () => {
    setActivation((old) => ({ ...old, loading: true }));
    const response = await SaaSServices.activateMemberships(activation.ids);
    const result = unwrapApiData(response);
    if (!result) {
      setActivation((old) => ({ ...old, loading: false }));
      return toast.error(apiErrorMessage(response, 'No users were activated.'));
    }
    const count = previewValue(result, ['activated_count', 'eligible_count', 'count']);
    setActivation({ open: false, scope: 'all', ids: undefined, preview: null, loading: false });
    await load();
    toast.success(`${count} user${count === 1 ? '' : 's'} activated`);
  };

  const preview = activation.preview || {};
  const eligibleCount = previewValue(preview, ['eligible_count', 'count'], Array.isArray(preview.eligible_membership_ids) ? preview.eligible_membership_ids.length : 0);
  const seatImpact = previewValue(preview, ['seat_impact', 'seats_required', 'seats_to_consume'], eligibleCount);
  const availableSeats = previewValue(preview, ['available_seats', 'seats_available'], seatSummary.available ?? 0);
  const shortage = previewValue(preview, ['seat_shortage', 'shortage'], Math.max(0, seatImpact - availableSeats));
  const activationBlocked = preview.can_activate === false || preview.sufficient_seats === false || shortage > 0 || eligibleCount === 0;
  const institutionName = currentUser.institution_name || currentUser.saas_context?.institution_name || 'Current institution';
  const roleInfoPostCloseAccess = postCloseAccessForInstitutionRole(roleInfo?.value, retentionDays || {});

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-slate-50 p-4 pb-10 lg:p-6 lg:pb-12">
      <div className="mx-auto max-w-[1680px] space-y-5">
        <header className="rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-blue-800 px-6 py-6 text-white shadow-lg">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-200">Institution administration</p>
          <div className="mt-2 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-3xl font-bold">Users &amp; Access</h1>
              <p className="mt-1 text-sm text-slate-300">{institutionName} · identities, named-user seats and permanent institution access</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" onClick={load} disabled={loading}>
                <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              {canMutateUsers && <Button variant="outline" className="border-white/30 bg-white/10 text-white hover:bg-white/20" onClick={() => setInviteOpen(true)}>
                <MailPlus className="mr-2 h-4 w-4" /> Invite User
              </Button>}
              {canMutateUsers && <Button className="bg-white text-blue-900 hover:bg-blue-50" onClick={beginAdd}>
                <Plus className="mr-2 h-4 w-4" /> Add User
              </Button>}
            </div>
          </div>
        </header>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5" aria-label="Institution user access summary">
          {[
            { label: 'Named-user seats', value: seatSummary.limit == null ? `${seatSummary.used} used` : `${seatSummary.used} / ${seatSummary.limit}`, hint: seatSummary.available == null ? 'Unified named users' : `${seatSummary.available} available`, icon: BadgeCheck },
            { label: 'Web enabled', value: seatSummary.webEnabled, hint: 'No additional seat', icon: Monitor },
            { label: 'Mobile enabled', value: seatSummary.mobileEnabled, hint: 'No additional seat', icon: Smartphone },
            { label: 'Pending invitations', value: seatSummary.pendingInvitations, hint: 'No seat until accepted and activated', icon: MailPlus },
            { label: 'Visible users', value: filteredUsers.length, hint: search ? 'Current search result' : 'Current institution', icon: Users },
          ].map(({ label, value, hint, icon: Icon }) => (
            <Card key={label} className="border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <Icon className="h-5 w-5 text-blue-600" />
                <p className="mt-3 text-2xl font-bold text-slate-900">{value}</p>
                <p className="text-sm font-semibold text-slate-700">{label}</p>
                <p className="mt-1 text-xs text-slate-500">{hint}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="h-11 pl-10"
                placeholder="Search name, email, employee ID, department, status, role or access…"
                aria-label="Smart user search"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Inactive', query: 'status:inactive' },
                { label: 'Web', query: 'access:web' },
                { label: 'Mobile', query: 'access:mobile' },
                { label: 'Institution Admin', query: 'role:institution_admin' },
              ].map(({ label, query }) => (
                <button key={query} type="button" onClick={() => setSearch(query)} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 hover:border-blue-300 hover:text-blue-700">
                  {label}
                </button>
              ))}
              {canMutateUsers && <Button variant="outline" onClick={() => openActivationPreview('all')}>
                <UserCheck className="mr-2 h-4 w-4" /> Activate All
              </Button>}
              {canMutateUsers && search.trim() && (
                <Button variant="outline" onClick={() => openActivationPreview('filtered')}>
                  Activate filtered users
                </Button>
              )}
            </div>
          </div>
        </section>

        <div className="grid min-h-[640px] gap-5 xl:grid-cols-[minmax(380px,0.9fr)_minmax(620px,1.6fr)]">
          <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="font-bold text-slate-900">Institution users</h2>
              <p className="text-xs text-slate-500">One person consumes one seat even when both applications are enabled.</p>
            </div>
            <div className="max-h-[760px] space-y-2 overflow-y-auto p-3">
              {loading && <p className="p-5 text-sm text-slate-500">Loading users…</p>}
              {!loading && filteredUsers.map((user) => (
                <button
                  key={user.id || user.membership_id}
                  type="button"
                  onClick={() => beginEdit(user)}
                  className={`w-full rounded-xl border p-4 text-left transition ${selectedUserId === user.id && !creating ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-blue-200 hover:bg-slate-50'}`}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block truncate font-semibold text-slate-900">{userLabel(user)}</span>
                      <span className="block truncate text-sm text-slate-500">{user.email || user.user_account}</span>
                    </span>
                    <Badge variant="outline" className={statusStyle(user.account_status)}>{user.account_status}</Badge>
                  </span>
                  <span className="mt-3 flex flex-wrap gap-1.5">
                    {user.web_enabled && <Badge variant="secondary"><Monitor className="mr-1 h-3 w-3" /> Web</Badge>}
                    {user.mobile_enabled && <Badge variant="secondary"><Smartphone className="mr-1 h-3 w-3" /> Mobile</Badge>}
                    {user.institution_roles?.map((role) => <Badge key={role} variant="outline">{roleLabel(role)}</Badge>)}
                  </span>
                  <span className="mt-3 block text-xs text-slate-500">{user.employee_id ? `Employee ${user.employee_id} · ` : ''}{user.departments_units?.length || 0} department(s)</span>
                </button>
              ))}
              {!loading && !filteredUsers.length && <p className="p-6 text-center text-sm text-slate-500">No institution users match this search.</p>}
            </div>

            {pendingInvitations.length > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 p-4">
                <h3 className="text-sm font-bold text-slate-800">Pending invitations ({pendingInvitations.length})</h3>
                <div className="mt-2 space-y-2">
                  {pendingInvitations.map((row) => (
                    <div key={row.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold">{row.email}</p>
                        <p className="text-[11px] text-slate-500">Expires {row.expires_at ? new Date(row.expires_at).toLocaleString() : 'as configured'}</p>
                      </div>
                      <div className="flex gap-1">
                        {row.invitation_token && <Button size="sm" variant="ghost" onClick={() => copyInvitation(row)} title="Copy acceptance link"><Copy className="h-4 w-4" /></Button>}
                        {canMutateUsers && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revokeInvitation(row.id)}>Revoke</Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900">{creating ? 'Add User' : selectedUser ? userLabel(selectedUser) : 'Select a user'}</h2>
                <p className="mt-1 text-sm text-slate-500">{creating ? 'Create an account directly. Invitation is the recommended onboarding method.' : selectedUser ? 'Account and permanent institution access.' : 'Choose a user on the left or add a new user.'}</p>
              </div>
              {canMutateUsers && selectedUser && !creating && (
                <div className="flex flex-wrap gap-2">
                  {selectedUser.account_status === 'active' ? (
                    <Button variant="outline" onClick={() => updateStatus(selectedUser, false)} disabled={saving}><Ban className="mr-2 h-4 w-4" /> Suspend</Button>
                  ) : selectedUser.account_status === 'inactive' ? (
                    <Button variant="outline" onClick={() => openActivationPreview('single', selectedUser)}><UserCheck className="mr-2 h-4 w-4" /> Activate</Button>
                  ) : selectedUser.account_status === 'suspended' ? (
                    <Button variant="outline" onClick={() => updateStatus(selectedUser, true)} disabled={saving}><UserCheck className="mr-2 h-4 w-4" /> Restore access</Button>
                  ) : (
                    <Badge variant="outline" className={statusStyle(selectedUser.account_status)}>Access cannot be activated</Badge>
                  )}
                  <Button variant="outline" className="text-red-600" onClick={() => setRemoveTarget(selectedUser)}><UserMinus className="mr-2 h-4 w-4" /> Remove from this institution</Button>
                </div>
              )}
            </div>

            {(creating || selectedUser) ? (
              <div className="space-y-7 p-6">
                <div>
                  <h3 className="flex items-center gap-2 font-bold text-slate-900"><IdCard className="h-4 w-4 text-blue-600" /> Platform identity</h3>
                  <p className="mt-1 text-xs text-amber-700">Name, email, phone, employee ID and password belong to the person’s platform identity. Changes apply across every institution they belong to.</p>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <label className="text-sm font-medium text-slate-700">Full name<Input disabled={!canMutateUsers} className="mt-1" value={form.full_name} onChange={(event) => updateForm('full_name', event.target.value)} placeholder="Full name" /></label>
                    <label className="text-sm font-medium text-slate-700">Email address<Input disabled={!canMutateUsers} className="mt-1" type="email" value={form.email} onChange={(event) => { updateForm('email', event.target.value); updateForm('user_account', event.target.value); }} placeholder="name@institution.org" /></label>
                    <label className="text-sm font-medium text-slate-700">Phone number<Input disabled={!canMutateUsers} className="mt-1" value={form.phone_number} onChange={(event) => updateForm('phone_number', event.target.value)} placeholder="Phone number" /></label>
                    <label className="text-sm font-medium text-slate-700">Employee ID<Input disabled={!canMutateUsers} className="mt-1" value={form.employee_id} onChange={(event) => updateForm('employee_id', event.target.value)} placeholder="Employee / staff ID" /></label>
                    <label className="text-sm font-medium text-slate-700 md:col-span-2">{creating ? 'Temporary password' : 'Reset platform password'}<Input disabled={!canMutateUsers} className="mt-1" type="password" value={form.password} onChange={(event) => updateForm('password', event.target.value)} placeholder={creating ? 'Required; the user must change it after sign-in' : 'Leave blank to keep the current password'} /></label>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h3 className="flex items-center gap-2 font-bold text-slate-900"><ShieldCheck className="h-4 w-4 text-blue-600" /> Institution membership</h3>
                  <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_320px]">
                    <RoleSelector selected={form.institution_roles} onChange={(value) => updateForm('institution_roles', value)} onRoleInfo={setRoleInfo} disabled={!canMutateUsers} />
                    <div className="space-y-3">
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3"><span className="flex items-center gap-2 text-sm font-semibold"><Monitor className="h-4 w-4" /> Web access</span><Switch disabled={!canMutateUsers} checked={form.web_enabled} onCheckedChange={(value) => updateForm('web_enabled', value)} /></div>
                        <div className="mt-4 flex items-center justify-between gap-3"><span className="flex items-center gap-2 text-sm font-semibold"><Smartphone className="h-4 w-4" /> Mobile access</span><Switch disabled={!canMutateUsers} checked={form.mobile_enabled} onCheckedChange={(value) => updateForm('mobile_enabled', value)} /></div>
                      </div>
                      <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                        <p className="font-semibold">Seat consumption</p>
                        <p className="mt-1 text-xs leading-relaxed">An active member consumes one named-user seat. Enabling both web and mobile still consumes only one seat.</p>
                      </div>
                      <label className="flex items-center justify-between rounded-xl border border-slate-200 p-4 text-sm font-semibold">
                        Account active
                        <Switch disabled={!canMutateUsers || creating || ['inactive', 'blocked'].includes(selectedUser?.account_status)} checked={form.is_active} onCheckedChange={(value) => updateForm('is_active', value)} />
                      </label>
                    </div>
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h3 className="font-bold text-slate-900">Organizational access</h3>
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Departments and units</label>
                      <DepartmentSelector disabled={!canMutateUsers} value={form.departments_units} onChange={(value) => updateForm('departments_units', value || [])} mode="multi" allowWildcard={false} placeholder="Select departments and units" className="mt-1 w-full" />
                      <p className="mt-1 text-xs text-slate-500">Leave blank when the user should not be restricted to specific departments.</p>
                    </div>
                    <label className="flex items-center justify-between gap-5 rounded-xl border border-slate-200 p-4">
                      <span><span className="block text-sm font-semibold text-slate-900">Nearby-incident awareness</span><span className="mt-1 block text-xs text-slate-500">Shows proximity awareness in addition to incidents personally assigned to the user.</span></span>
                      <Switch disabled={!canMutateUsers} checked={form.enable_nearby_awareness} onCheckedChange={(value) => updateForm('enable_nearby_awareness', value)} />
                    </label>
                  </div>
                </div>

                {canMutateUsers && <div className="flex justify-end gap-2 border-t border-slate-200 pt-5">
                  <Button variant="outline" onClick={() => { setCreating(false); setSelectedUserId(null); setForm(EMPTY_FORM); }}>Cancel</Button>
                  <Button onClick={saveUser} disabled={saving}>{saving ? 'Saving…' : creating ? 'Add User' : 'Save changes'}</Button>
                </div>}
              </div>
            ) : (
              <div className="flex min-h-[520px] flex-col items-center justify-center p-8 text-center text-slate-500">
                <Users className="h-12 w-12 text-slate-300" />
                <p className="mt-4 font-semibold text-slate-700">No user selected</p>
                <p className="mt-1 max-w-sm text-sm">Select an institution member to manage access, or add/invite a new user.</p>
              </div>
            )}
          </section>
        </div>
      </div>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Invite User</DialogTitle><DialogDescription>Recommended onboarding: the person accepts a one-time invitation and establishes their account.</DialogDescription></DialogHeader>
          <form onSubmit={createInvitation} className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-[1fr_170px]">
              <label className="text-sm font-medium">Email address<Input className="mt-1" type="email" required value={invite.email} onChange={(event) => setInvite((old) => ({ ...old, email: event.target.value }))} placeholder="name@institution.org" /></label>
              <label className="text-sm font-medium">Expires in hours<Input className="mt-1" type="number" min="1" max="720" value={invite.expires_in_hours} onChange={(event) => setInvite((old) => ({ ...old, expires_in_hours: Number(event.target.value) }))} /></label>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-800">Departments and units</label>
              <DepartmentSelector
                value={invite.departments_units}
                onChange={(departments_units) => setInvite((old) => ({ ...old, departments_units: departments_units || [] }))}
                mode="multi"
                allowWildcard={false}
                placeholder="Select the user’s departments and units"
                className="mt-1 w-full"
              />
              <p className="mt-1 text-xs text-slate-500">Only departments in the current institution are available. These assignments are carried securely by the invitation.</p>
            </div>
            <div><p className="mb-2 text-sm font-semibold">Permanent institution roles</p><RoleSelector selected={invite.institution_roles} onChange={(institution_roles) => setInvite((old) => ({ ...old, institution_roles }))} onRoleInfo={setRoleInfo} /></div>
            <div className="flex flex-wrap gap-5 rounded-xl border border-slate-200 p-4">
              <label className="flex items-center gap-2 text-sm"><Switch checked={invite.web_enabled} onCheckedChange={(web_enabled) => setInvite((old) => ({ ...old, web_enabled }))} /> Web access</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={invite.mobile_enabled} onCheckedChange={(mobile_enabled) => setInvite((old) => ({ ...old, mobile_enabled }))} /> Mobile access</label>
            </div>
            <p className="text-xs text-slate-500">A pending invitation consumes no named-user seat. Capacity is checked atomically when the membership is activated.</p>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button><Button type="submit" disabled={saving}><MailPlus className="mr-2 h-4 w-4" /> Create invitation</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(issuedInvitation)} onOpenChange={(open) => !open && setIssuedInvitation(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Invitation ready to send</DialogTitle><DialogDescription>{issuedInvitation?.copied ? 'The one-time link was copied. Send it to the invited user.' : 'Copy this one-time link and send it to the invited user.'} DisasterMS does not send an email automatically.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input readOnly value={issuedInvitation?.acceptance_url || ''} className="font-mono text-xs" aria-label="One-time invitation acceptance link" />
            <Button type="button" variant="outline" className="w-full" onClick={async () => {
              try {
                await navigator.clipboard.writeText(issuedInvitation?.acceptance_url || '');
                toast.success('Acceptance link copied');
              } catch (_) {
                toast.error('Could not copy the link. Select it and copy manually.');
              }
            }}><Copy className="mr-2 h-4 w-4" /> Copy acceptance link</Button>
          </div>
          <DialogFooter><Button onClick={() => setIssuedInvitation(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={activation.open} onOpenChange={(open) => !activation.loading && setActivation((old) => ({ ...old, open }))}>
        <DialogContent>
          <DialogHeader><DialogTitle>{activation.scope === 'filtered' ? 'Activate filtered users' : activation.scope === 'single' ? 'Activate user' : 'Activate All'}</DialogTitle><DialogDescription>The server validates eligibility and named-user seat capacity before changing any membership.</DialogDescription></DialogHeader>
          {activation.loading && !activation.preview ? <p className="py-6 text-sm text-slate-500">Checking eligibility and seat capacity…</p> : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold">{eligibleCount}</p><p className="text-xs text-slate-500">Eligible users</p></div>
                <div className="rounded-xl border border-slate-200 p-4"><p className="text-2xl font-bold">{seatImpact}</p><p className="text-xs text-slate-500">Seats consumed</p></div>
              </div>
              <p className="text-sm text-slate-600">Available seats: <strong>{availableSeats}</strong></p>
              {shortage > 0 && <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">Activation stopped before any changes: {shortage} additional named-user seat{shortage === 1 ? '' : 's'} required.</p>}
              {preview.excluded_count > 0 && <p className="text-xs text-slate-500">{preview.excluded_count} suspended, blocked, removed, pending or otherwise ineligible record(s) will not be activated.</p>}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setActivation((old) => ({ ...old, open: false }))} disabled={activation.loading}>Cancel</Button><Button onClick={confirmActivation} disabled={activation.loading || activationBlocked}><CheckCircle2 className="mr-2 h-4 w-4" /> Confirm activation</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(removeTarget)} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove from this institution?</DialogTitle><DialogDescription>{userLabel(removeTarget)} will immediately lose access to this institution. Their platform identity and memberships in other institutions will not be deleted.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setRemoveTarget(null)}>Cancel</Button><Button variant="destructive" onClick={removeMembership} disabled={saving}><UserMinus className="mr-2 h-4 w-4" /> Remove from this institution</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(roleInfo)} onOpenChange={(open) => !open && setRoleInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{roleInfo?.label || 'Institution role'}</DialogTitle>
            <DialogDescription>Permanent institution role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm leading-6 text-slate-600">{ROLE_HELP[roleInfo?.value]}</p>
            {roleInfoPostCloseAccess && (
              <>
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Post-close read-only access</p>
                  <p className="mt-1 text-lg font-bold text-blue-950">
                    {retentionDays == null ? 'Loading institution policy…' : postCloseAccessLabel(roleInfoPostCloseAccess.days)}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-blue-800">
                    {roleInfo?.value === 'executive_oversight'
                      ? 'Uses this institution’s Executive Oversight post-close reviewer window.'
                      : 'Uses this institution’s Compliance Auditor post-close access window.'}
                  </p>
                </div>
                <p className="text-xs leading-5 text-slate-500">This value is display-only here. Institution Administrators configure it under Policies &amp; Support → Retention.</p>
              </>
            )}
          </div>
          <DialogFooter><Button type="button" onClick={() => setRoleInfo(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
